import React, {
  DragEvent,
  MouseEvent,
  useEffect,
  useContext,
  useMemo,
  useState,
} from 'react';
import clsx from 'clsx';
import {
  CellContext,
} from '@tanstack/react-table';
import IconButton from '@mui/material/IconButton';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditIcon from 'core/icons/edit';
import UndoIcon from '@mui/icons-material/Undo';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  formatMoney,
  notEmptyString,
} from '@populateui/utils';
import ToolTip from 'core/components/Tooltip';
import type {
  ClaimAdjustment,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/EOBAdjustmentCodePopover';
import {
  paymentsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  ServiceAllocation,
} from 'pages/Dashboard/pages/Billing/types';
import {
  canMoveAdjustmentToTarget,
  AdjustmentExceptionMap,
  getEffectiveAdjustmentTarget,
  moveAdjustmentTarget,
  withAdjustmentTargets,
} from 'pages/Dashboard/pages/Billing/Payments/utils';
import {
  isServiceLineInactive,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/serviceLineStatus';
import {
  AdjustmentRelatedField,
  getAdjustmentBucketStyle,
} from 'pages/Dashboard/pages/Billing/Payments/constants';
import {
  WizardContext,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/wizardContext';

const derivedAdjustmentTooltip = 'Edit CARC codes to change this amount';
const adjustmentDragDataType = 'application/x-populate-adjustment-target';

export type AdjustmentBucketField = 'insuranceAdjustments' | 'autoWriteOff';

type AdjustmentDragPayload = {
  rowId: string;
  adjustmentIndex: number;
  effectiveBucket: AdjustmentRelatedField;
  defaultBucket: AdjustmentRelatedField;
};

type AdjustmentCodePill = {
  adjustment: ClaimAdjustment;
  adjustmentIndex: number;
  effectiveBucket: AdjustmentBucketField;
};

type DraggingState = {
  adjustmentIndex: number;
  rowId: string;
  effectiveBucket: AdjustmentBucketField;
} | null;

function getAdjustmentCode(adjustment: ClaimAdjustment) {
  const groupCode = adjustment?.groupCode?.code ?? '';
  const reasonCode = adjustment?.reasonCode?.code ?? '';

  return (notEmptyString(groupCode) && notEmptyString(reasonCode))
    ? `${groupCode}-${reasonCode}`
    : 'Missing code';
}

function getAdjustmentPillKey(
  adjustment: ClaimAdjustment,
  adjustmentIndex: number,
) {
  return [
    adjustmentIndex,
    adjustment?.claimAdjustmentId ?? '',
    getAdjustmentCode(adjustment),
    adjustment?.amount ?? '',
  ].join('-');
}

function getOppositeAdjustmentBucket(
  field: AdjustmentBucketField,
): AdjustmentBucketField {
  return field === 'insuranceAdjustments' ? 'autoWriteOff' : 'insuranceAdjustments';
}

function getMoveTargetTooltip(field: AdjustmentBucketField) {
  return field === 'autoWriteOff'
    ? 'Move to auto write-off'
    : 'Move to insurance adjustments';
}

function getAdjustmentDragPayload(
  event: DragEvent<HTMLDivElement>,
): AdjustmentDragPayload | null {
  const rawPayload = event.dataTransfer.getData(adjustmentDragDataType);

  if (!rawPayload) {
    return null;
  }

  try {
    return JSON.parse(rawPayload) as AdjustmentDragPayload;
  } catch {
    return null;
  }
}

type AdjustmentPillProps = {
  adjustment: ClaimAdjustment;
  adjustmentExceptionMap: AdjustmentExceptionMap;
  adjustmentIndex: number;
  disabled: boolean;
  isDragging: boolean;
  rowId: string;
  effectiveBucket: AdjustmentBucketField;
  onDragEnd: () => void;
  onDragStart: (payload: DraggingState) => void;
  onMove: (adjustmentIndex: number, effectiveBucket: AdjustmentRelatedField) => void;
};

function AdjustmentPill({
  adjustment,
  adjustmentExceptionMap,
  adjustmentIndex,
  disabled,
  isDragging,
  rowId,
  effectiveBucket,
  onDragEnd,
  onDragStart,
  onMove,
}: AdjustmentPillProps) {
  const destinationBucket = getOppositeAdjustmentBucket(effectiveBucket);
  const canMove = !disabled && canMoveAdjustmentToTarget(
    adjustment,
    destinationBucket,
    adjustmentExceptionMap,
  );
  const isMoved = effectiveBucket !== adjustment.defaultBucket;
  const canUndo = (
    !disabled
    && isMoved
    && canMoveAdjustmentToTarget(
      adjustment,
      adjustment.defaultBucket ?? effectiveBucket,
      adjustmentExceptionMap,
    )
  );
  const canMoveToOppositeBucket = canMove && !canUndo;
  const isLocked = !canMove && !canUndo;
  const defaultStyle = getAdjustmentBucketStyle(adjustment.defaultBucket);
  const pillClassName = clsx(
    defaultStyle.backgroundClassName,
    defaultStyle.borderClassName,
    defaultStyle.textClassName,
  );

  const handleDragStart = (event: DragEvent<HTMLDivElement>) => {
    if (!canMove) {
      event.preventDefault();
      return;
    }

    const payload: AdjustmentDragPayload = {
      rowId,
      adjustmentIndex,
      effectiveBucket,
      defaultBucket: adjustment.defaultBucket ?? effectiveBucket,
    };

    onDragStart({
      adjustmentIndex,
      rowId,
      effectiveBucket,
    });
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData(adjustmentDragDataType, JSON.stringify(payload));
  };

  const handleMoveToOppositeBucket = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onMove(adjustmentIndex, destinationBucket);
  };

  const handleUndo = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onMove(adjustmentIndex, adjustment.defaultBucket ?? effectiveBucket);
  };

  return (
    <div
      className={clsx(
        'inline-flex min-w-0 items-center gap-0.5 rounded-md border px-1 py-0.5',
        'text-[10px] font-bold leading-none shadow-sm',
        { 'cursor-grab active:cursor-grabbing': canMove },
        { 'opacity-40': isDragging },
        pillClassName,
      )}
      draggable={canMove}
      onDragEnd={onDragEnd}
      onDragStart={handleDragStart}
    >
      {canMove && <DragIndicatorIcon className="!h-3.5 !w-3.5 shrink-0" />}
      <span className="truncate">{getAdjustmentCode(adjustment)}</span>
      <span className={clsx(
        'shrink-0 font-semibold',
        defaultStyle.amountTextClassName,
      )}
      >
        {formatMoney(Number(adjustment?.amount ?? 0))}
      </span>
      {canMoveToOppositeBucket && (
        <ToolTip title={getMoveTargetTooltip(destinationBucket)}>
          <IconButton
            size="small"
            className="!-mr-1 !p-0.5"
            onClick={handleMoveToOppositeBucket}
          >
            <ArrowForwardIcon className={clsx('!h-3 !w-3', defaultStyle.textClassName)} />
          </IconButton>
        </ToolTip>
      )}
      {canUndo && (
        <ToolTip title="Move back">
          <IconButton
            size="small"
            className="!-mr-1 !p-0.5"
            onClick={handleUndo}
          >
            <UndoIcon className={clsx('!h-3 !w-3', defaultStyle.textClassName)} />
          </IconButton>
        </ToolTip>
      )}
      {isLocked && <LockOutlinedIcon className="!h-3 !w-3 shrink-0 !text-gray-500" />}
    </div>
  );
}

type Props = {
  row: CellContext<ServiceAllocation, unknown>['row'];
  field: AdjustmentBucketField;
  readonly?: boolean;
  isExplicitEdit?: boolean;
};

export default function AdjustmentCodeAmountCell({
  row,
  field,
  readonly,
  isExplicitEdit,
}: Props) {
  const disabled = readonly || isServiceLineInactive(row.original);
  const { adjustmentExceptionMap } = useContext(WizardContext);
  const rowId = row.id;
  const [draggingState, setDraggingState] = useState<DraggingState>(null);
  const adjustmentPills = useMemo<AdjustmentCodePill[]>(() => {
    const normalizedAdjustments = withAdjustmentTargets(
      row.original?.adjustments ?? [],
      adjustmentExceptionMap,
    );

    return normalizedAdjustments
      .map((adjustment, adjustmentIndex) => ({
        adjustment,
        adjustmentIndex,
        effectiveBucket: getEffectiveAdjustmentTarget(adjustment, adjustmentExceptionMap),
      }))
      .filter((item) => item.effectiveBucket === field)
      .map(({ adjustment, adjustmentIndex }) => ({
        adjustment,
        adjustmentIndex,
        effectiveBucket: field,
      }));
  }, [adjustmentExceptionMap, field, row.original?.adjustments]);

  useEffect(() => {
    if (draggingState) {
      const hasDraggingPill = adjustmentPills.some(({
        adjustmentIndex,
        effectiveBucket,
      }) => (
        draggingState.adjustmentIndex === adjustmentIndex
        && draggingState.rowId === rowId
        && draggingState.effectiveBucket === effectiveBucket
      ));

      if (!hasDraggingPill) {
        setDraggingState(null);
      }
    }
  }, [adjustmentPills, draggingState, rowId]);

  const handleMove = (
    adjustmentIndex: number,
    effectiveBucket: AdjustmentRelatedField,
  ) => {
    const adjustments = row.original?.adjustments ?? [];
    const nextAdjustments = moveAdjustmentTarget(
      adjustments,
      adjustmentIndex,
      effectiveBucket,
      adjustmentExceptionMap,
    );

    if (nextAdjustments !== adjustments) {
      paymentsEventChannel.emit('onChangeEOBAdjustmentCode', {
        index: row.index,
        adjustments: nextAdjustments,
        data: row.original,
      });
    }

    setDraggingState(null);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (disabled) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const payload = getAdjustmentDragPayload(event);

    if (disabled || payload?.rowId !== rowId) {
      setDraggingState(null);
      return;
    }

    handleMove(payload.adjustmentIndex, field);
  };

  return (
    <div
      className="flex h-full w-full flex-col overflow-hidden rounded-md"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <div className="flex flex-1 flex-col items-start justify-center gap-1 p-1">
        <ToolTip title={isExplicitEdit ? 'Edit' : derivedAdjustmentTooltip}>
          <div className="flex items-center gap-1 font-semibold text-gray-900">
            <span>{formatMoney(Number(row.original?.[field] ?? 0))}</span>
            {isExplicitEdit ? (
              <IconButton
                size="small"
                className="!p-0.5"
                onClick={(event) => {
                  paymentsEventChannel.emit('onEditEOBPatientAdjustments', {
                    anchorEl: event.currentTarget,
                    patientId: row.original?.patientId ?? 0,
                    field,
                  });
                }}
              >
                <EditIcon className="!h-4 !w-4 !text-gray-600" />
              </IconButton>
            ) : (
              <InfoOutlinedIcon className="!h-4 !w-4 !text-gray-500" />
            )}
          </div>
        </ToolTip>
        <div className="flex w-full flex-col items-start gap-1">
          {adjustmentPills.map(({
            adjustment,
            adjustmentIndex,
            effectiveBucket,
          }) => (
            <AdjustmentPill
              key={getAdjustmentPillKey(adjustment, adjustmentIndex)}
              adjustment={adjustment}
              adjustmentExceptionMap={adjustmentExceptionMap}
              adjustmentIndex={adjustmentIndex}
              disabled={disabled}
              isDragging={Boolean(
                draggingState
                && draggingState.adjustmentIndex === adjustmentIndex
                && draggingState.rowId === rowId
                && draggingState.effectiveBucket === effectiveBucket,
              )}
              onDragEnd={() => { setDraggingState(null); }}
              onDragStart={(payload) => { setDraggingState(payload); }}
              rowId={rowId}
              effectiveBucket={effectiveBucket}
              onMove={handleMove}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
